import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import type { HighlightSpec } from "@/composition/api/content";
import { FilterField } from "@/types/enums.ts";

import useSavedSearchesStore from "./savedSearches.ts";

const useQuerySearchStore = defineStore("search:querySearch", () => {
  const savedSearchesStore = useSavedSearchesStore();
  const route = useRoute();
  const searchQuery = ref("");
  const selectedFilterFields = ref<FilterField[]>([]);

  /**
   * Assigns the current search query param to searchQuery.
   * Runs on init and on every `route.fullPath` change.
   */
  function syncFromRoute() {
    const searchStringQuery = (route.query.search as string | string[]) ?? "";
    searchQuery.value = Array.isArray(searchStringQuery)
      ? searchStringQuery.join(" AND ")
      : searchStringQuery;
  }

  watch(() => route.fullPath, syncFromRoute, { immediate: true });

  watch(
    () => route.query.filterFields,
    (newValue) => {
      if (newValue) {
        selectedFilterFields.value = [
          ...(route.query.filterFields as FilterField[]),
        ];
      }
    },
    {
      immediate: true,
    },
  );

  async function executeSearch() {
    if (searchQuery.value.length === 0) {
      await resetSearchQuery();
      return;
    }
  }

  const hasSearchQuery = computed(() => {
    return searchQuery.value.length > 0;
  });

  function editSearchQuery(query: string) {
    searchQuery.value = query;
  }

  async function resetSearchQuery() {
    searchQuery.value = "";
    selectedFilterFields.value = [];
    savedSearchesStore.resetActiveSavedSearchId();
  }

  const normalHighlights: HighlightSpec[] = [
    {
      field: FilterField.TEASER,
    },
    {
      field: FilterField.HEADLINE,
    },
    {
      field: FilterField.CONTENT,
    },
    {
      field: FilterField.KEYWORD,
    },
  ];

  const hasSelectedFilterFields = computed(() => {
    return selectedFilterFields.value?.length > 0;
  });

  const highlightSpecs = computed<HighlightSpec[]>(() => {
    if (selectedFilterFields.value.length > 0) {
      return selectedFilterFields.value.map((value) => {
        return {
          field: value,
        };
      });
    }
    return normalHighlights;
  });

  function setFilterFields(filterFields: FilterField[]) {
    selectedFilterFields.value = [...filterFields];
  }

  function addFilterField(field: FilterField) {
    selectedFilterFields.value = [...selectedFilterFields.value, field];
  }

  function removeFilterField(field: FilterField) {
    selectedFilterFields.value = selectedFilterFields.value.filter(
      (selectedFilterField) => selectedFilterField !== field,
    );
  }

  function toggleFilterField(field: FilterField) {
    if (selectedFilterFields.value.includes(field)) {
      removeFilterField(field);
    } else {
      addFilterField(field);
    }
  }

  return {
    executeSearch,
    searchQuery,
    hasSearchQuery,
    editSearchQuery,
    resetSearchQuery,
    highlightSpecs,
    selectedFilterFields,
    hasSelectedFilterFields,
    setFilterFields,
    toggleFilterField,
  };
});

export default useQuerySearchStore;
